import React, { useState } from 'react'

import IconDone from 'client/icons/Done'
import styles from './styles.module.css'
import cn from 'classnames'
import Dropdown from 'src/comps/Atom/Drowpdown'
import { Text } from 'src/comps/Atom/Text'
import ArrowUpDown from 'client/icons/ArrowUpDown'
import ArrowDown from 'client/icons/ArrowDown'

interface Option<T, Z> {
  key: T
  value: Z
}

interface ISelectProps<T, Z> {
  readonly label?: string
  readonly triggerVariant: 'sort' | 'select'
  readonly onChange: (value: Option<T, Z>) => void
  readonly options: Option<T, Z>[]
  readonly defaultValue?: Option<T, Z>
  readonly align?: 'start' | 'center' | 'end'
}

function Select<T, Z>({
  label,
  triggerVariant,
  onChange,
  options,
  defaultValue,
  align = 'center',
}: ISelectProps<T, Z>) {
  const [isOpen, setIsOpen] = useState(false)

  const [currentOrder, setCurrentOrder] = useState(defaultValue ?? options[0])

  function handleSelectOrder(newOrder: Option<T, Z>) {
    setCurrentOrder(newOrder)
    onChange(newOrder)
    setIsOpen(false)
  }

  const trigger = (
    <div
      data-testid="trigger"
      className={cn(styles.trigger, {
        [styles.triggerSort]: triggerVariant === 'sort',
        [styles.triggerSelect]: triggerVariant === 'select',
        [styles.active]: isOpen,
      })}
    >
      <Text variant="captionL" ellipsis>
        {label && <Text as="span">{label} &nbsp;</Text>}

        <Text as="span" weight="weight3">
          {currentOrder.value}
        </Text>
      </Text>
      {triggerVariant === 'sort' && <ArrowUpDown size={16} />}
      {triggerVariant === 'select' && <ArrowDown size={16} />}
    </div>
  )

  return (
    <Dropdown
      show={isOpen}
      onShowChange={setIsOpen}
      align={align}
      showClose={false}
      trigger={trigger}
    >
      <ul className={styles.list}>
        {options.map(order => {
          const { key, value } = order
          const isCurrentOrder = key === currentOrder.key

          return (
            <li key={`${key}`}>
              <button
                type="button"
                className={cn(styles.option, {
                  [styles.selected]: isCurrentOrder,
                })}
                onClick={() => handleSelectOrder(order)}
                role="menuitem"
              >
                <Text variant="textBodyL" weight={isCurrentOrder ? 'weight3' : 'weight2'}>
                  {value}
                </Text>
                <IconDone size={24} />
              </button>
            </li>
          )
        })}
      </ul>
    </Dropdown>
  )
}

export default Select
